import * as React from "react";

import type { AuditLogEntry } from "api/auditLogs";
import { Card, CardBody, CardHeader, Skeleton } from "Components/ui";
import {
  Image,
  Info,
  KeyRound,
  Lock,
  Pencil,
  Plus,
  Trash2,
  Unlock,
  UserRound,
} from "Components/ui/icons";

interface RecentActivityCardProps {
  canView: boolean;
  loading: boolean;
  failed: boolean;
  entries: AuditLogEntry[];
}

const formatRelativeTime = (value?: string): string => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) {
    return "Just now";
  }
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }
  return date.toLocaleDateString();
};

/** "USER_PROFILE_UPDATED" → "Profile Updated", for actions without their own wording. */
const formatActionLabel = (action: string): string =>
  action
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/^user\s+/i, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word.length <= 3 && word === word.toUpperCase()
        ? word
        : `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}`
    )
    .join(" ");

/** One readable line per audit entry; covers the USER_* actions the backend writes. */
const describe = (entry: AuditLogEntry): { label: string; icon: React.ReactNode } => {
  const actor = entry.actorName || entry.targetName || "Someone";
  const target = entry.targetName || "a user";
  // The actor acted on someone else (an admin resetting a password, deleting a user...).
  const onOther = Boolean(
    entry.actorName && entry.targetName && entry.actorName !== entry.targetName
  );
  const onSelf = Boolean(entry.actorName && entry.actorName === entry.targetName);

  switch ((entry.action || "").toUpperCase()) {
    case "USER_LOGIN":
      return { label: `${actor} signed in`, icon: <UserRound /> };
    case "USER_PROFILE_UPDATED":
      return { label: `${actor} updated their profile`, icon: <Pencil /> };
    case "USER_PROFILE_PIC_UPDATED":
      return { label: `${actor} changed their profile photo`, icon: <Image /> };
    case "USER_RESET_PASSWORD":
    case "USER_PASSWORD_RESET":
      return {
        label: onOther ? `${actor} reset ${target}'s password` : `${actor} reset their password`,
        icon: <KeyRound />,
      };
    case "USER_CREATE":
      return { label: `${actor} added ${target}`, icon: <Plus /> };
    case "USER_UPDATE":
      return { label: `${actor} edited ${onSelf ? "their account" : target}`, icon: <Pencil /> };
    case "USER_SUSPEND":
      return { label: `${actor} suspended ${target}`, icon: <Lock /> };
    case "USER_ACTIVATE":
      return { label: `${actor} activated ${target}`, icon: <Unlock /> };
    case "USER_DELETE":
    case "USER_DELETED":
      return {
        label: onOther ? `${actor} deleted ${target}` : `${entry.targetName || actor} was deleted`,
        icon: <Trash2 />,
      };
    default: {
      const action = entry.action ? formatActionLabel(entry.action) : "";
      return { label: action ? `${actor}: ${action}` : actor, icon: <Info /> };
    }
  }
};

/** The three latest USER_* audit entries; the page reloads them every 30 seconds. */
const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  canView,
  loading,
  failed,
  entries,
}) => {
  let body: React.ReactNode;
  if (!canView) {
    body = (
      <p className="text-sm text-muted-foreground">You need audit log access to see activity.</p>
    );
  } else if (loading) {
    body = (
      <div className="space-y-4">
        {[0, 1, 2].map((index) => (
          <div key={index} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (failed) {
    body = (
      <p className="text-sm text-danger-text">
        Activity didn't load. It tries again every 30 seconds.
      </p>
    );
  } else if (entries.length === 0) {
    body = <p className="text-sm text-muted-foreground">No recent user activity.</p>;
  } else {
    body = (
      <ul className="space-y-4">
        {entries.map((entry) => {
          const { label, icon } = describe(entry);
          return (
            <li key={entry._id} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-muted text-muted-foreground [&_svg]:h-4 [&_svg]:w-4"
              >
                {icon}
              </span>
              <div className="min-w-0">
                <p className="text-sm text-foreground">{label}</p>
                <time
                  dateTime={entry.timestamp}
                  title={new Date(entry.timestamp).toLocaleString()}
                  className="text-xs text-muted-foreground"
                >
                  {formatRelativeTime(entry.timestamp)}
                </time>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Card>
      <CardHeader title="Recent activity" description="User changes and sign-ins." />
      <CardBody>{body}</CardBody>
    </Card>
  );
};

export default RecentActivityCard;
